import { Product } from '@/context/types';
import useCatalog from '@/hooks/useCatalog';
import useLocalCart from '@/hooks/useLocalCart';
import { Card } from '../CartCard/CartCard';
import { motion, AnimatePresence } from 'framer-motion';
import { useMemo, useState } from 'react';
import { useModalActions } from '@/components/Modals/useModalActions';
import { MODAL_KEY } from '@/constants';

export const SkeletonLoader = () => (
  <div className="flex flex-col gap-12 w-full animate-pulse px-4 sm:px-6 lg:px-8 py-10 max-w-screen-2xl mx-auto mt-20 bg-brand-frost">
    <div className="h-12 bg-brand-truffle/10 rounded w-1/2 mx-auto mb-10"></div>
    {[1, 2].map((section) => (
      <div key={section} className="space-y-8">
        <div className="h-8 bg-brand-truffle/10 rounded w-1/3 md:w-1/4"></div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-6">
          {[1, 2, 3, 4, 5].map((card) => (
            <div
              key={card}
              className="aspect-[3/4] bg-brand-truffle/5 rounded-[var(--radius-artisan)]"
            ></div>
          ))}
        </div>
      </div>
    ))}
  </div>
);

export default () => {
  const { fillBox, addLineItem, updateLineItemQuantity, cartDict, totalItems } =
    useLocalCart();
  const { hierarchy } = useCatalog();
  const { openModal } = useModalActions(MODAL_KEY.R_DRAWER);
  // 1. State for the Elegant Selector
  const categories = useMemo(
    () => Object.keys(hierarchy?.standardSections ?? {}),
    [hierarchy],
  );
  const [activeCategory, setActiveCategory] = useState(categories[0] || '');

  if (!hierarchy) return <SkeletonLoader />;

  return (
    <div id="ttc-menu" className="relative min-h-screen bg-brand-frost pb-32">
      <div className="max-w-screen-2xl mx-auto px-6 md:px-12 flex flex-col gap-24 md:gap-32 py-20">
        {/* 2. Mix & Match: The "Asymmetrical Hero" Section */}
        {Object.keys(hierarchy?.mixAndMatchBoxes ?? {}).map((bundle) => {
          const bMix = bundle.toLowerCase().trim();
          const boxProducts = (
            Object.values(hierarchy?.boxes?.[bMix] ?? {}) as Product[]
          ).sort((a, b) => {
            const capA = (a as any)?.capacity || 0;
            const capB = (b as any)?.capacity || 0;

            // capA - capB sorts Ascending (e.g., 2 ct, 6 ct, 12 ct)
            return capA - capB;
          });

          const MixTitle = bMix.replace('mix:', '');

          return (
            <section
              key={bundle}
              className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start"
            >
              <div className="lg:col-span-4 lg:sticky lg:top-32">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-[1px] bg-brand-burgundy" />
                  <span className="text-brand-burgundy font-display font-bold text-xs uppercase tracking-widest">
                    Signature Boxes
                  </span>
                </div>
                <h2 className="font-display text-4xl text-brand-truffle mb-6 capitalize leading-tight">
                  {MixTitle}
                </h2>
                <p className="text-text-muted font-body mb-8 leading-relaxed">
                  Our handcrafted boxes are designed for perfect preservation
                  and presentation. Choose a size to begin your customization
                  journey.
                </p>
              </div>

              <div className="lg:col-span-8">
                <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-center justify-center gap-y-6">
                  {boxProducts.map((product) => (
                    <div
                      key={product.id}
                      className="w-full h-full flex justify-center items-center transition-transform duration-500 hover:-translate-y-2"
                    >
                      <div className="w-[80%] h-[80%] sm:w-[90%] sm:h-[90%] md:w-full md:h-full">
                        <Card
                          product={product}
                          selectedQty={0}
                          onAdd={() => fillBox(product, bundle)}
                          onRemove={() => {}}
                          hideReadMore
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          );
        })}

        {/* --- 3. Standard Sections: The Filtered Gallery --- */}
        <section className="flex flex-col items-center">
          {/* Modernized Section Header */}
          <div className="flex flex-col items-center gap-4 w-full mb-12 text-center">
            <div className="flex items-center gap-4 w-full">
              <div className="h-[1px] flex-1 bg-brand-truffle/10" />
              <h3 className="font-display text-brand-truffle/50 text-[10px] md:text-xs uppercase tracking-[0.3em] px-4 shrink-0">
                Artisanal Provisions
              </h3>
              <div className="h-[1px] flex-1 bg-brand-truffle/10" />
            </div>
          </div>

          {/* Refined Category Selector (Segmented Pill Style) */}
          <div className="mb-16 w-full flex justify-center px-4">
            <div className="inline-flex bg-brand-cream/50 p-1 rounded-full shadow-xs border border-brand-truffle/10 w-full max-w-2xl backdrop-blur-sm">
              {categories.map((cat) => {
                const isSelected = activeCategory === cat;
                return (
                  <button
                    key={cat}
                    onClick={() => setActiveCategory(cat)}
                    className={`relative flex-1 rounded-full py-2 md:py-2.5 text-[10px] md:text-xs font-display font-bold uppercase tracking-widest transition-all duration-300 ease-out ${
                      isSelected
                        ? 'bg-brand-truffle text-brand-cream shadow-sm'
                        : 'text-text-muted hover:text-brand-truffle'
                    }`}
                  >
                    {/* Using a motion span for a subtle text shift when active */}
                    <span className="relative z-10">{cat}</span>

                    {/* Optional: Add a LayoutGroup motion div here if you want a sliding background effect */}
                    {isSelected && (
                      <motion.div
                        layoutId="segmentedControl"
                        className="absolute inset-0 bg-brand-truffle rounded-full"
                        transition={{
                          type: 'spring',
                          stiffness: 300,
                          damping: 30,
                        }}
                      />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Grid with AnimatePresence Shuffling */}
          <div className="w-full">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeCategory}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{
                  duration: 0.4,
                  ease: [0.22, 1, 0.36, 1],
                }}
                className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-8 gap-y-12"
              >
                {hierarchy?.standardSections[activeCategory]?.map(
                  (product: Product) => {
                    const cartQty = cartDict?.[product.id]?.quantity || 0;
                    return (
                      <Card
                        key={product.id}
                        product={product}
                        selectedQty={cartQty}
                        onAdd={() =>
                          !cartQty
                            ? addLineItem({
                                ...product,
                                quantity: 1,
                                localId: window.crypto.randomUUID(),
                              })
                            : updateLineItemQuantity(product.id, cartQty + 1)
                        }
                        onRemove={() =>
                          updateLineItemQuantity(product.id, cartQty - 1)
                        }
                      />
                    );
                  },
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </section>
      </div>
      {/* --- NEW: Floating Call-To-Action Bar --- */}
      <AnimatePresence>
        {totalItems > 0 && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 260, damping: 20 }}
            className="fixed bottom-6 left-0 right-0 z-50 flex justify-center px-4 pointer-events-none"
          >
            <div className="flex items-center justify-between w-full max-w-lg bg-brand-cream/90 backdrop-blur-md border border-brand-truffle/10 shadow-lift rounded-full py-3 px-6 pointer-events-auto">
              <div className="flex items-center gap-3">
                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-brand-truffle/10 text-brand-truffle font-display font-bold text-sm">
                  {totalItems}
                </span>
                <span className="font-body text-sm font-medium text-text-heading">
                  Items selected
                </span>
              </div>

              {/* Note: Update the onClick to route to your actual cart/checkout path */}
              <button
                className="px-6 py-2.5 bg-[var(--color-brand-burgundy)] hover:bg-[var(--color-brand-burgundy)]/90 text-white rounded-full font-display uppercase tracking-widest text-xs transition-colors shadow-sm"
                onClick={openModal}
              >
                Review Order
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
